<template>
	<v-container
		class="asset-view"
		:class="{ 'has-online-availability': hasOnlineAppointments }"
	>
		<div v-if="category">
			<v-row>
				<v-col class="col-12">
					<v-card>
						<v-card-text>
							<co-html-widget
								class="widget text--grey"
								v-if="category.widget_id"
								:id="category.widget_id"
							></co-html-widget>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
			<v-row>
				<v-col class="col-12">
					<v-card class="mb-6">
						<v-card-text>
							<v-row>
								<v-col class="col-12 col-md-8">
									<v-select
										:readonly="hasOnlyOneLocation"
										class="mb-4 select-location"
										:hide-details="true"
										:items="locations"
										:label="$t('reserve.locationLabel')"
										item-text="name"
										item-value="id"
										placeholder="Select a location..."
										:clearable="hasManyLocations"
										v-model="selectedAssetLocation"
									></v-select>
									<div
										v-if="isMultiplierEnabled"
										class="mt-4"
									>
										<v-select
											v-if="isSelectType"
											:items="multiplierOptions"
											item-text="label"
											item-value="value"
											:label="questionText"
											v-model="selectedMultiplier"
											placeholder="Please select..."
											:clearable="true"
											class="mb-4"
										></v-select>
										<v-checkbox
											v-else
											:label="questionText"
											v-model="selectedMultiplier"
											:true-value="checkboxValue"
											:false-value="1"
											class="mb-4"
										></v-checkbox>
									</div>
									<v-date-picker
										:picker-date.sync="pickerDate"
										:full-width="true"
										:landscape="windowWidth > 600"
										v-model="selectedDate"
										@click="getTypes()"
										color="lighten-1"
										header-color="#f2f2f2"
										:events="setDayStatusMarkers"
										class="date-picker v-card--flat v-sheet"
										flat
										:allowed-dates="allowedDates"
										:min="minDate"
										:max="maxDate"
									>
										<v-card
											class="legend-card pt-1 pb-0"
											aria-label="Availability legend"
											flat
										>
											<v-card-text>
												<ul
													class="legend"
													v-if="hybridEnabled()"
												>
													<li
														class="caption inperson-asset"
													>
														<div
															class="legend-icon"
														>
															<span
																class="legend-key dot inperson-asset"
															></span>
														</div>
														<span
															class="legend-label"
														>
															In person
															availability
														</span>
													</li>
													<li
														class="caption online-asset"
													>
														<div
															class="legend-icon online-asset-container"
														>
															<span
																class="legend-key dot online-asset"
															>
															</span>
														</div>
														<span
															class="legend-label"
														>
															Virtual availability
														</span>
													</li>
												</ul>
											</v-card-text>
										</v-card>
										<v-overlay
											v-if="loading"
											contained
											transition="fade-transition"
											class="align-center justify-center"
										>
											<v-progress-circular
												class="mt-10"
												:indeterminate="true"
												:rotate="0"
												:size="32"
												:value="0"
												:width="4"
												color="white"
											></v-progress-circular>
										</v-overlay>
									</v-date-picker>
								</v-col>
								<v-col class="col-12 col-md-4">
									<div
										v-if="
											selectedLocation &&
												isMultiplierSelected
										"
									>
										<div
											class="text-center headline message-box"
										>
											{{
												$t(
													'assets.selectAppointmentTime'
												)
											}}
										</div>
										<co-asset-group
											class="mx-auto"
											:id="selectedLocation.id"
											:multiplier="
												selectedMultiplier || 1
											"
										></co-asset-group>
									</div>
									<div
										v-else-if="!selectedLocation"
										class="text-center headline message-box"
									>
										{{ $t('assets.selectLocation') }}
									</div>
									<div
										v-else-if="
											isMultiplierEnabled &&
												isSelectType &&
												!isMultiplierSelected
										"
										class="text-center headline message-box"
									>
										{{ multiplierUnselectedMessage }}
									</div>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
			<v-row v-if="selectedLocation">
				<v-card class="mx-3 mb-6" width="100%">
					<v-card-text>
						<co-html-widget
							class="widget"
							v-if="selectedLocation.widget_id"
							:id="selectedLocation.widget_id"
						></co-html-widget>
						<div
							v-if="selectedLocation"
							v-html="
								groupDetails(selectedLocation.id).instructions
							"
						></div>
					</v-card-text>
				</v-card>
			</v-row>
			<v-row v-if="selectedLocation">
				<v-col :class="[showMap ? 'col-md-6' : 'col-md-12', 'col-12']">
					<co-location-details
						v-if="selectedLocation"
						:id="selectedLocation.location_id"
					></co-location-details>
				</v-col>

				<v-col fill-height class="col-12 col-md-6" v-if="showMap">
					<v-card height="100%" class="mb-6">
						<v-card-title>{{ $t('global.map') }} </v-card-title>
						<co-location-map
							class="mb-6"
							v-if="selectedLocation"
							:id="selectedLocation.location_id"
						></co-location-map>
					</v-card>
				</v-col>
			</v-row>
		</div>
	</v-container>
</template>
<script>
import CoHtmlWidget from '../components/html-widget.vue';
import CoAssetGroup from '../components/asset-group.vue';
import CoLocationDetails from '../components/location-details.vue';
import CoLocationMap from '../components/location-map.vue';

import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
	components: {
		CoHtmlWidget,
		CoAssetGroup,
		CoLocationDetails,
		CoLocationMap
	},
	data() {
		return {
			loaded: false,
			timeRange: [0, 0],
			keyword: '',
			windowWidth: window.innerWidth,
			assetTypes: [],
			pickerDate: '',
			loading: false,
			selectedMultiplier: null
		};
	},
	created() {
		this.keyword = window.config.keyword;
	},
	beforeMount() {
		var category = this.category;
		if (category) {
			if (!category.name) {
				window.location.href = '/404';
			}
		}
	},
	mounted() {
		this.$store.state.showMap = false;
		window.addEventListener('resize', () => {
			this.windowWidth = window.innerWidth;
		});
		Promise.all([
			this.$store.dispatch('fetchClientSettings'),
			this.$store.dispatch('fetchAssetCategory', this.id)
		]).then(() => {
			this.loaded = true;
			if (
				!this.selectedDate ||
				moment(this.selectedDate, 'YYYY-MM-DD').isBefore(moment())
			) {
				this.selectedDate = moment().format('YYYY-MM-DD');
			}
			this.pickerDate = this.selectedDate;
			this.getTypes();
			const restrictedDates = this.getGroupDateRestrictions(this.id);
			if (restrictedDates !== null) {
				this.selectedDate =
					restrictedDates[0] || moment().format('YYYY-MM-DD');
				this.pickerDate = this.selectedDate;
			}
			this.initializeMultiplier();
		});
	},

	methods: {
		initializeMultiplier() {
			if (this.isMultiplierEnabled && this.multiplierOptions.length > 0) {
				if (this.isSelectType) {
					this.selectedMultiplier = null;
				} else {
					this.selectedMultiplier = Number(
						this.multiplierOptions[0].value
					);
				}
			} else {
				this.selectedMultiplier = 1;
			}
		},
		groupDetails(id) {
			return this.$store.getters.getAssetGroupForId(id) || {};
		},

		async getTypes() {
			if (!this.selectedLocation || !this.isMultiplierSelected) {
				this.assetTypes = [];
				return;
			}

			this.loading = true;
			await this.$store.dispatch('fetchAssetType', {
				id: this.selectedLocation?.id || 0,
				selectedDate: this.pickerDate,
				multiplier: this.selectedMultiplier || 1
			});
			this.assetTypes = this.$store.state.assetType;
			this.loading = false;
		},
		setDayStatusMarkers(date) {
			let id = (this.selectedLocation || {}).id;
			if (!id) return '';

			if (!this.isMultiplierSelected) return '';
			let now = new Date();
			now.setHours(0, 0, 0, 0);
			let requestedDate = new Date(date);
			requestedDate.setHours(0, 0, 0, 0);
			if (requestedDate < now) {
				return '';
			}
			let group = this.groupDetails(id);
			if (group.hybrid_appointments_enabled) {
				return [
					this.assetTypes[date]?.INPERSON
						? 'day-dot inperson-asset'
						: '',
					this.assetTypes[date]?.ONLINE ? 'day-dot online-asset' : ''
				];
			} else {
				return this.assetTypes[date]?.INPERSON
					? 'day-dot inperson-asset'
					: '';
			}
		},
		hybridEnabled() {
			return this.$store.state.clientSettings
				.feature_schedule_hybrid_appointments;
		},
		getGroupDateRestrictions(groupId) {
			switch (groupId) {
				case 668:
					return ['2022-09-08', '2022-09-09'];
					break;
				case 670:
					return ['2022-09-29', '2022-09-30'];
					break;
				case 671:
					return ['2022-11-03', '2022-11-04'];
					break;
				case 672:
					return ['2022-10-18', '2022-10-19'];
					break;
				case 673:
					return ['2022-10-18', '2022-10-19'];
					break;
				case 674:
					return ['2022-11-01'];
					break;
				case 706:
					return ['2022-12-08'];
					break;
				case 731:
					return ['2023-01-28', '2023-01-29', '2023-01-30'];
					break;
				case 740:
					return ['2023-03-28', '2023-03-29', '2023-03-30'];
					break;
				case 742:
					return [
						'2023-03-15',
						'2023-03-16',
						'2023-03-17',
						'2023-03-18'
					];
					break;
				case 795:
					return [
						'2023-06-23',
						'2023-06-24',
						'2023-06-25',
						'2023-06-26'
					];
					break;
				case 896:
					return ['2024-04-03', '2024-04-04', '2024-04-05'];
					break;
				case 1125:
					return [
						'2025-06-27',
						'2025-06-28',
						'2025-06-29',
						'2025-06-30'
					];
					break;
				case 1230:
					return ['2026-03-17', '2026-03-18', '2026-03-19'];
					break;
				default:
					return null;
			}
		}
	},
	watch: {
		pickerDate: {
			handler: function() {
				this.getTypes();
			}
		},
		selectedAssetLocation: {
			handler: function(val) {
				if (this.locations) {
					var locations = this.locations.find(
						l => this.selectedAssetLocation === l.id
					);
					if (locations) {
						var minDate = moment(this.minDate).format('YYYY-MM-DD');
						if (minDate != this.selectedDate) {
							this.selectedDate = minDate;
						}
						this.initializeMultiplier();
						this.getTypes();
					}
				}
			}
		},
		multiplierOptions: {
			handler: function(newOptions) {
				if (
					newOptions.length > 0 &&
					this.selectedMultiplier !== null &&
					this.selectedMultiplier !== undefined
				) {
					this.selectedMultiplier = Number(this.selectedMultiplier);
				}
			}
		},
		selectedMultiplier: {
			handler: function(newValue) {
				if (
					newValue !== null &&
					newValue !== undefined &&
					typeof newValue !== 'number'
				) {
					this.selectedMultiplier = Number(newValue);
				}
				this.getTypes();
			}
		}
	},
	computed: {
		...mapGetters(['getAssetCategoryForId']),
		isMultiplierEnabled() {
			return this.assetGroup?.multiplier_enabled;
		},
		isSelectType() {
			return this.assetGroup?.multiplier_question_type === 'select';
		},
		isMultiplierSelected() {
			if (!this.isMultiplierEnabled) return true;
			if (this.isSelectType) {
				return (
					this.selectedMultiplier !== null &&
					this.selectedMultiplier !== undefined
				);
			}
			return true;
		},
		multiplierOptions() {
			return (
				this.assetGroup?.multiplier_question_options.map(option => ({
					label: option.label,
					value: Number(option.value)
				})) || []
			);
		},
		multiplierUnselectedMessage() {
			return (
				this.assetGroup?.multiplier_unselected_message ||
				'Please select an option'
			);
		},
		questionText() {
			return this.assetGroup?.multiplier_question || '';
		},
		checkboxValue() {
			return Number(
				this.assetGroup?.multiplier_question_checkbox_value || 1
			);
		},
		assetGroup() {
			if (!this.selectedAssetLocation) return null;
			return this.groupDetails(this.selectedAssetLocation);
		},
		formattedDate() {
			const format = this.$vuetify.breakpoint.smAndUp
				? 'D MMM YYYY'
				: 'D MMM';
			return this.selectedDate
				? moment(this.selectedDate, 'YYYY-MM-DD').format(format)
				: '';
		},
		hasOnlineAppointments() {
			const group = this.groupDetails(this.selectedAssetLocation);
			return group.hybrid_appointments_enabled;
		},
		allowedDates() {
			const restrictedDates = this.getGroupDateRestrictions(this.id);
			if (restrictedDates === null) {
				return null;
			} else {
				return date => restrictedDates.includes(date);
			}
		},
		selectedLocation() {
			return this.locations.find(
				l => this.selectedAssetLocation === l.id
			);
		},
		hasOnlyOneLocation() {
			let locationCount = this.locations.length;
			return locationCount === 1;
		},
		hasManyLocations() {
			let locationCount = this.locations.length;
			return locationCount > 1;
		},
		locations() {
			let locs = this.category.locations
				.reduce((ret, location) => {
					return [
						...ret,
						...location.groups.map(g => ({
							id: g.id,
							location_id: location.location_id,
							widget_id: g.widget_id,
							name: `${location.name} - ${g.name}`
						}))
					].sort((a, b) =>
						a.name < b.name ? -1 : a.name > b.name ? 1 : 0
					);
				}, [])
				.map(value => {
					value.id = parseInt(value.id);
					value.widget_id = parseInt(value.widget_id);
					return value;
				});

			if ('restrictGroup' in this.$route.query) {
				let groupId = parseInt(this.$route.query.restrictGroup);
				locs = locs.filter(loc => {
					return loc.id === groupId;
				});
			}

			if (1 === locs.length) {
				this.$store.commit(
					'selectedAssetLocation',
					parseInt(locs[0].id)
				);
			} else if ('defaultGroup' in this.$route.query) {
				let groupId = parseInt(this.$route.query.defaultGroup);
				this.$store.commit('selectedAssetLocation', parseInt(groupId));
			}
			return locs;
		},
		clientSettings() {
			return this.$store.state.clientSettings;
		},
		showMap() {
			return this.$store.state.showMap;
		},
		category() {
			let cat = this.getAssetCategoryForId(parseInt(this.id));
			if (cat) cat.widget_id = parseInt(cat.widget_id);
			return cat;
		},
		id() {
			return parseInt(this.$route.params.id) || null;
		},
		minDate() {
			return moment().format('YYYY-MM-DD');
		},
		maxDate() {
			const defaultMaxDate = moment()
				.add(365, 'days')
				.format('YYYY-MM-DD');

			if (this.selectedLocation) {
				const bookableDaysInAdvance = this.groupDetails(
					this.selectedLocation.id
				).booking_days_advance;

				if (bookableDaysInAdvance && bookableDaysInAdvance > 0) {
					return moment()
						.add(bookableDaysInAdvance, 'days')
						.format('YYYY-MM-DD');
				} else {
					return defaultMaxDate;
				}
			} else {
				return defaultMaxDate;
			}
		},
		selectedAssetLocation: {
			get() {
				return this.$store.state.selectedAssetLocation;
			},
			set(value) {
				this.$store.commit('selectedAssetLocation', value);
			}
		},
		selectedDate: {
			get() {
				return this.$store.state.selectedDate;
			},
			set(value) {
				this.$store.commit('selectedDate', value);
			}
		}
	}
};
</script>
<style lang="scss">
$radius: 9px;
$date-btn-height: 32px;

#app {
	.asset-view {
		.v-text-field fieldset,
		.v-text-field .v-input__control,
		.v-text-field .v-input__slot {
			border-radius: $radius;
		}

		.v-picker__title {
			border-radius: $radius;
		}

		.v-picker__actions.v-card__actions {
			padding-bottom: 0;
		}

		.select-location .v-select__selection--comma {
			line-height: 24px;
		}
	}

	div.message-box {
		padding: 10px !important;
		font-size: 16px !important;
		background-color: #f2f2f2 !important;
		border-radius: 10px !important;
		margin-bottom: 16px !important;
	}
}

.header {
	width: 100%;
	height: 100px;
}

.legend-card {
	.legend-title {
		font-size: small;
	}

	ul.legend {
		list-style-type: none;
		display: inline-block;
		padding-left: 0;

		li.caption {
			color: black;
			display: inline-flex;
		}

		li {
			.legend-icon {
				display: inline-block;
				width: 20px;
				text-align: left;
				margin-right: 8px;
			}

			.legend-icon.online-asset-container {
				padding-left: 2px;
			}
		}

		li:first-child {
			.legend-label {
				margin-right: 32px;
			}
		}
	}
}

.legend-key {
	width: 20px;
	height: 20px;
	display: inline-block;
	border-radius: 50%;
	vertical-align: middle;
}

.container.asset-view.has-online-availability
	.date-picker
	.legend-card
	ul.legend
	li.caption.online-asset {
	display: inline-flex;
}

.date-picker {
	.day-dot.inperson-asset,
	.legend-key.inperson-asset {
		background-color: #0ea9bd !important;
		border-color: #0ea9bd !important;
	}

	.day-dot.online-asset,
	.legend-key.online-asset {
		background-color: transparent !important;
		border-color: #2a9d4c !important;
		border: 2px solid;
	}

	.legend-card ul.legend li.caption.online-asset {
		display: none;
	}

	.v-date-picker-table {
		height: fit-content;
		padding-bottom: 1em;

		th {
			font-size: 20px;
		}

		.v-btn {
			font-size: 20px;
		}

		td {
			padding-bottom: 5px;
		}

		.v-date-picker-table__events {
			padding-top: 2px;
			bottom: 0;
		}

		tr {
			height: 60px;
		}
	}

	/* date picker table buttons */
	.v-date-picker-table--date {
		td {
			button.v-btn {
				/* Transparent border keeps rounded corners without failing
				   WCAG 1.4.11 non-text contrast (a solid #ffffff border on a
				   white cell produced a 1:1 ratio). Visible state colours
				   still come from .v-btn--active / .v-btn--disabled below. */
				border: 1px solid transparent !important;
				border-radius: 4px;
			}

			/* button for todays date */
			button.v-btn.v-date-picker-table__current {
				color: #000000 !important;
				caret-color: #000000 !important;
				border-radius: 4px;
				border: 1px solid #000000 !important;

				.v-date-picker-table__events {
					margin-bottom: 0;
				}
			}

			/* button for selected date */
			button.v-btn.v-btn--active:before {
				opacity: 0;
			}

			button.v-btn.v-btn--active {
				border-radius: 4px;
				background-color: #000000 !important;
				border-color: #000000 !important;
				border: 1px solid #000000 !important;
			}

			/* button for disabled date */
			button.v-btn.v-btn--disabled.v-date-picker-table__current,
			button.v-btn.v-btn--active.v-date-picker-table__current {
				color: #ffffff !important;
			}
		}
	}
}
</style>
